import React, { useState, useRef, useEffect } from 'react';

const Browser = ({ theme, url: initialUrl }) => {
  const [url, setUrl] = useState(initialUrl || 'https://duckduckgo.com');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchPage, setIsSearchPage] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const iframeRef = useRef(null);
  const searchInputRef = useRef(null);

  // Use the initialUrl prop when it changes
  useEffect(() => {
    if (initialUrl && initialUrl !== url) {
      setUrl(initialUrl);
      setLoading(true);
      setError(null);
      
      // Check if it's a DuckDuckGo search URL
      if (initialUrl.includes('duckduckgo://search')) {
        // Extract search query from the URL if present
        const queryParam = new URLSearchParams(initialUrl.replace('duckduckgo://search', '')).get('q');
        if (queryParam) {
          setSearchQuery(queryParam);
          setIsSearchPage(true);
          // Fetch results using the DuckDuckGo API
          performDuckDuckGoSearch(queryParam);
        } else {
          setIsSearchPage(true);
          setSearchQuery('');
          setSearchResults([]);
        }
      } else if (initialUrl.includes('duckduckgo.com')) {
        setIsSearchPage(true);
      } else {
        setIsSearchPage(false);
      }
    }
  }, [initialUrl]);

  // Focus search input on the centered search page
  useEffect(() => {
    if (isSearchPage && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isSearchPage]);

  // List of websites known to work in iframes
  const safeWebsites = [
    { name: "DuckDuckGo", url: "https://duckduckgo.com" },
    { name: "Wikipedia", url: "https://en.m.wikipedia.org" },
    { name: "Mojeek", url: "https://www.mojeek.com/lite/" },
    { name: "Brave Search", url: "https://search.brave.com" },
    { name: "Bing", url: "https://www.bing.com" },
    { name: "GitHub", url: "https://github.com" },
    { name: "HTML5 Test", url: "https://html5test.com" },
    { name: "Hacker News", url: "https://news.ycombinator.com" },
    { name: "JSFiddle", url: "https://jsfiddle.net" },
    { name: "CodePen", url: "https://codepen.io" }
  ];

  // Function to perform search using DuckDuckGo API
  const performDuckDuckGoSearch = async (query) => {
    if (!query) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    
    try {
      // Create the API URL with the query
      const apiUrl = `https://api.duckduckgo.com/?q=${encodeURIComponent(query)}&format=json&no_html=1&no_redirect=1`;
      
      // List of CORS proxies to try
      const corsProxies = [
        "", // Try direct first (might fail due to CORS)
        "https://corsproxy.io/?",
        "https://api.allorigins.win/raw?url=",
        "https://proxy.cors.sh/",
      ];
      
      let response = null;
      let error = null;
      
      // Try each proxy until one works
      for (const proxy of corsProxies) {
        try {
          const fullUrl = proxy ? proxy + encodeURIComponent(apiUrl) : apiUrl;
          console.log(`Trying with proxy: ${proxy || 'Direct'} - ${fullUrl}`);
          
          const resp = await fetch(fullUrl, {
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          
          if (resp.ok) {
            response = await resp.json();
            console.log('Search results received:', response);
            break;
          }
        } catch (e) {
          console.log(`Error with proxy ${proxy || 'Direct'}:`, e);
          error = e;
        }
      }
      
      if (!response) {
        throw new Error(error ? error.message : "Failed to fetch search results");
      }
      
      // Process the results
      const formattedResults = [];
      
      // Add the abstract/instant answer if available
      if (response.Abstract) {
        formattedResults.push({
          type: 'abstract',
          title: response.Heading || query,
          content: response.Abstract,
          url: response.AbstractURL,
          source: response.AbstractSource
        });
      }
      
      // Add definition if available
      if (response.Definition) {
        formattedResults.push({
          type: 'definition',
          title: 'Definition',
          content: response.Definition,
          url: response.DefinitionURL,
          source: response.DefinitionSource
        });
      }
      
      // Add instant answer if available
      if (response.Answer) {
        formattedResults.unshift({
          type: 'answer',
          content: response.Answer,
          title: 'Instant Answer'
        });
      }
      
      // Add related topics
      if (response.RelatedTopics && response.RelatedTopics.length > 0) {
        response.RelatedTopics.forEach(topic => {
          if (topic.Result) {
            // Extract title from HTML
            const titleMatch = topic.Result.match(/<a[^>]*>(.*?)<\/a>/);
            const title = titleMatch ? titleMatch[1] : 'Related Topic';
            
            // Clean text
            let content = topic.Text || '';
            if (!content && topic.Result) {
              content = topic.Result.replace(/<[^>]*>/g, '');
            }
            
            formattedResults.push({
              type: 'topic',
              title: title,
              content: content,
              url: topic.FirstURL,
              icon: topic.Icon?.URL
            });
          } else if (topic.Topics) {
            topic.Topics.forEach(subTopic => {
              const titleMatch = subTopic.Result?.match(/<a[^>]*>(.*?)<\/a>/);
              const title = titleMatch ? titleMatch[1] : 'Related Topic';
              
              formattedResults.push({
                type: 'topic',
                title: title,
                content: subTopic.Text || '',
                url: subTopic.FirstURL,
                icon: subTopic.Icon?.URL,
                category: topic.Name
              });
            });
          }
        });
      }
      
      // If no results were found, create some fallback results
      if (formattedResults.length === 0) {
        formattedResults.push({
          type: 'info',
          title: 'No results found',
          content: `No direct results found for "${query}". Try visiting the DuckDuckGo website for more results.`,
          url: `https://duckduckgo.com/?q=${encodeURIComponent(query)}`
        });
        
        // Add a link to search on Wikipedia
        formattedResults.push({
          type: 'topic',
          title: `Search for "${query}" on Wikipedia`,
          content: `Find information about "${query}" on Wikipedia, the free encyclopedia.`,
          url: `https://en.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(query)}`
        });
      }
      
      setSearchResults(formattedResults);
    } catch (error) {
      console.error("Error searching DuckDuckGo:", error);
      
      // Fallback results when API fails
      setSearchResults([
        {
          type: 'error',
          title: 'Search Error',
          content: `Unable to get search results for "${query}". ${error.message}`,
          url: null
        },
        {
          type: 'topic',
          title: `Search for "${query}" on DuckDuckGo`,
          content: `Try viewing the results directly on DuckDuckGo's website.`,
          url: `https://duckduckgo.com/?q=${encodeURIComponent(query)}`
        }
      ]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    let processedUrl = url.trim();
    
    // If the URL doesn't start with http:// or https://, add https://
    if (!/^https?:\/\//i.test(processedUrl)) {
      // Check if it's a URL or a search term
      if (/\.[a-z]{2,}(\/|$)/i.test(processedUrl)) {
        processedUrl = `https://${processedUrl}`;
        setIsSearchPage(false);
      } else {
        // Search using DuckDuckGo
        setIsSearchPage(true);
        setSearchQuery(processedUrl);
        performDuckDuckGoSearch(processedUrl);
        processedUrl = `duckduckgo://search?q=${encodeURIComponent(processedUrl)}`;
        return;
      }
    } else {
      setIsSearchPage(false);
    }
    
    setUrl(processedUrl);
  };

  const handleCenteredSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setLoading(true);
      setError(null);
      setIsSearchPage(true);
      performDuckDuckGoSearch(searchQuery);
      setUrl(`duckduckgo://search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleGoHome = () => {
    setIsSearchPage(true);
    setSearchQuery('');
    setSearchResults([]);
    setUrl('https://duckduckgo.com');
    setError(null);
  };

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError("This website cannot be displayed in an iframe. Try visiting one of the safe websites.");
  };

  // Handle clicking on a search result
  const handleResultClick = (result) => {
    if (result.url) {
      setUrl(result.url);
      setLoading(true);
      setError(null);
      setIsSearchPage(false);
    }
  };

  // Render the centered DuckDuckGo search experience
  const renderSearchPage = () => {
    if (searchQuery && searchResults.length > 0) {
      return renderSearchResults();
    }
    
    return (
      <div style={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme === 'dark' ? '#222' : '#f7f7f7',
        color: theme === 'dark' ? '#fff' : '#333',
        padding: '20px'
      }}>
        {/* DuckDuckGo Logo */}
        <div style={{ marginBottom: '30px' }}>
          <img 
            src="https://duckduckgo.com/assets/logo_homepage.normal.v108.svg" 
            alt="DuckDuckGo" 
            style={{ height: '80px' }}
          />
        </div>
        
        {/* Search Form */}
        <form 
          onSubmit={handleCenteredSearch}
          style={{ 
            width: '100%', 
            maxWidth: '560px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <div style={{
            width: '100%',
            display: 'flex',
            position: 'relative',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
          }}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 50px 12px 20px',
                borderRadius: '4px',
                border: theme === 'dark' ? '1px solid #444' : '1px solid #ddd',
                backgroundColor: theme === 'dark' ? '#333' : 'white',
                color: theme === 'dark' ? 'white' : 'black',
                fontSize: '16px',
                outline: 'none'
              }}
              placeholder="Search the web without being tracked"
            />
            <button
              type="submit"
              style={{
                position: 'absolute',
                right: '0',
                top: '0',
                height: '100%',
                width: '50px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: theme === 'dark' ? '#aaa' : '#666'
              }}
            >
              <i className="fas fa-search"></i>
            </button>
          </div>
          
          <div style={{ marginTop: '30px', display: 'flex', gap: '10px' }}>
            <button
              type="submit"
              style={{
                padding: '10px 20px',
                backgroundColor: theme === 'dark' ? '#444' : '#f3f3f3',
                color: theme === 'dark' ? 'white' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              DuckDuckGo Search
            </button>
            <button
              type="button"
              onClick={() => {
                const randomSite = safeWebsites[Math.floor(Math.random() * safeWebsites.length)];
                setUrl(randomSite.url);
                setLoading(true);
                setError(null);
                setIsSearchPage(false);
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: theme === 'dark' ? '#444' : '#f3f3f3',
                color: theme === 'dark' ? 'white' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              I'm Feeling Lucky
            </button>
          </div>
        </form>
        
        {/* Quick Links */}
        <div style={{ 
          marginTop: '40px', 
          display: 'flex', 
          gap: '15px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          maxWidth: '600px'
        }}>
          {safeWebsites.slice(0, 8).map((site, index) => (
            <button
              key={index}
              onClick={() => {
                setUrl(site.url);
                setLoading(true);
                setError(null);
                setIsSearchPage(site.url.includes('duckduckgo.com'));
              }}
              style={{
                padding: '8px 12px',
                backgroundColor: theme === 'dark' ? '#333' : 'white',
                color: theme === 'dark' ? 'white' : '#333',
                border: theme === 'dark' ? '1px solid #444' : '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <i className="fas fa-globe" style={{ fontSize: '12px' }}></i>
              {site.name}
            </button>
          ))}
        </div>
        
        {/* Privacy message */}
        <div style={{ 
          marginTop: '30px', 
          fontSize: '13px',
          color: theme === 'dark' ? '#aaa' : '#666',
          textAlign: 'center'
        }}>
          <p>DuckDuckGo doesn't track you. That's our privacy policy in a nutshell.</p>
          <p style={{ marginTop: '5px' }}>No tracking, no ad targeting, just searching.</p>
        </div>
      </div>
    );
  };
  
  // Render search results
  const renderSearchResults = () => {
    if (isSearching) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%', 
          backgroundColor: theme === 'dark' ? '#222' : '#f7f7f7',
          color: theme === 'dark' ? '#fff' : '#333' 
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>
              <i className="fas fa-circle-notch fa-spin"></i>
            </div>
            <p>Searching for "{searchQuery}"...</p>
          </div>
        </div>
      );
    }
    
    return (
      <div style={{ 
        height: '100%', 
        overflowY: 'auto',
        backgroundColor: theme === 'dark' ? '#222' : '#f8f8f8',
        color: theme === 'dark' ? '#eee' : '#333',
        padding: '0'
      }}>
        {/* Search header */}
        <div style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: theme === 'dark' ? '#1a1a1a' : '#fff',
          padding: '15px 20px',
          borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#e0e0e0'}`,
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          zIndex: 5,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img 
              src="https://duckduckgo.com/assets/logo_header.v108.svg" 
              alt="DuckDuckGo" 
              style={{ height: '30px', marginRight: '15px' }}
            />
            <form 
              onSubmit={handleCenteredSearch}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <div style={{ 
                position: 'relative',
                width: '300px' 
              }}>
                <input 
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 35px 8px 15px',
                    borderRadius: '20px',
                    border: `1px solid ${theme === 'dark' ? '#444' : '#ddd'}`,
                    backgroundColor: theme === 'dark' ? '#333' : 'white',
                    color: theme === 'dark' ? 'white' : 'black',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
                <button
                  type="submit"
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: theme === 'dark' ? '#aaa' : '#666',
                    cursor: 'pointer'
                  }}
                >
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </form>
          </div>
        </div>
        
        {/* Results container */}
        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto', 
          padding: '20px' 
        }}>
          {searchResults.map((result, index) => (
            <div 
              key={index}
              style={{
                backgroundColor: theme === 'dark' 
                  ? (result.type === 'answer' ? 'rgba(70, 130, 220, 0.2)' : '#2a2a2a') 
                  : (result.type === 'answer' ? 'rgba(70, 130, 220, 0.1)' : 'white'),
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '15px',
                border: `1px solid ${theme === 'dark' ? '#333' : '#e0e0e0'}`,
                cursor: result.url ? 'pointer' : 'default'
              }}
              onClick={() => result.url && handleResultClick(result)}
            >
              {/* Result title */}
              <div style={{ 
                fontWeight: 'bold', 
                fontSize: result.type === 'abstract' || result.type === 'answer' ? '1.2rem' : '1rem',
                marginBottom: '5px',
                color: result.url 
                  ? (theme === 'dark' ? '#4a90e2' : '#1a0dab') 
                  : (theme === 'dark' ? '#eee' : '#333')
              }}>
                {result.title}
              </div>
              
              {/* URL display */}
              {result.url && (
                <div style={{ 
                  color: theme === 'dark' ? '#8ab4f8' : '#006621',
                  fontSize: '0.85rem',
                  marginBottom: '8px',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {result.url}
                </div>
              )}
              
              {/* Result content */}
              <div style={{ 
                color: theme === 'dark' ? '#ccc' : '#444',
                fontSize: '0.95rem',
                lineHeight: '1.4'
              }}>
                {result.content}
              </div>
              
              {/* Source display */}
              {result.source && (
                <div style={{ 
                  marginTop: '5px',
                  fontSize: '0.8rem',
                  color: theme === 'dark' ? '#888' : '#777'
                }}>
                  Source: {result.source}
                </div>
              )}
              
              {/* Category display */}
              {result.category && (
                <div style={{ 
                  marginTop: '5px',
                  fontSize: '0.8rem',
                  color: theme === 'dark' ? '#888' : '#777'
                }}>
                  Category: {result.category}
                </div>
              )}
            </div>
          ))}
          
          {/* Attribution footer */}
          <div style={{ 
            textAlign: 'center', 
            padding: '20px 0', 
            color: theme === 'dark' ? '#888' : '#777',
            fontSize: '0.8rem'
          }}>
            <p>Results provided by DuckDuckGo API - Privacy focused and tracking-free</p>
            <p style={{ marginTop: '5px' }}>
              <button
                onClick={() => {
                  const url = `https://duckduckgo.com/?q=${encodeURIComponent(searchQuery)}`;
                  setUrl(url);
                  setLoading(true);
                  setError(null);
                  setIsSearchPage(false);
                }}
                style={{
                  marginTop: '8px',
                  padding: '8px 15px',
                  backgroundColor: theme === 'dark' ? '#333' : '#f3f3f3',
                  color: theme === 'dark' ? '#fff' : '#333',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                See more results on DuckDuckGo.com
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: theme === 'dark' ? 'rgba(20, 20, 35, 0.8)' : 'rgba(240, 240, 245, 0.8)',
      backdropFilter: 'blur(20px)',
      color: theme === 'dark' ? 'white' : 'black'
    }}>
      {/* Browser Navigation Bar */}
      <div style={{
        display: 'flex',
        padding: '8px 10px',
        backgroundColor: theme === 'dark' ? 'rgba(30, 30, 50, 0.5)' : 'rgba(220, 220, 235, 0.5)',
        borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`
      }}>
        <button 
          style={{ 
            backgroundColor: 'transparent', 
            border: 'none',
            color: theme === 'dark' ? '#ccc' : '#555',
            padding: '0 8px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
          title="Home"
          onClick={handleGoHome}
        >
          <i className="fas fa-home"></i>
        </button>
        
        <button 
          style={{ 
            backgroundColor: 'transparent', 
            border: 'none',
            color: theme === 'dark' ? '#ccc' : '#555',
            padding: '0 8px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
          title="Refresh"
          onClick={() => {
            if (isSearchPage) {
              if (searchQuery) {
                performDuckDuckGoSearch(searchQuery);
              } else {
                setSearchQuery('');
                setSearchResults([]);
              }
            } else {
              setLoading(true);
              setError(null);
              if (iframeRef.current) {
                iframeRef.current.src = url;
              }
            }
          }}
        >
          <i className="fas fa-sync"></i>
        </button>
        
        {/* URL Input Bar */}
        <form 
          style={{ flex: 1, margin: '0 8px' }}
          onSubmit={handleSubmit}
        >
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: '15px',
              border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              backgroundColor: theme === 'dark' ? 'rgba(40, 40, 70, 0.6)' : 'rgba(245, 245, 250, 0.6)',
              color: theme === 'dark' ? 'white' : 'black',
              fontSize: '14px'
            }}
            placeholder="Search or enter website URL"
          />
        </form>
      </div>
      
      {/* Safe websites dropdown */}
      <div style={{ 
        padding: '8px 15px', 
        backgroundColor: theme === 'dark' ? 'rgba(30, 30, 50, 0.5)' : 'rgba(220, 220, 235, 0.5)',
        borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        display: 'flex',
        overflowX: 'auto',
        gap: '10px'
      }}>
        {safeWebsites.map((site, index) => (
          <button 
            key={index}
            onClick={() => {
              setUrl(site.url);
              setLoading(true);
              setError(null);
              setIsSearchPage(site.url.includes('duckduckgo.com'));
              if (iframeRef.current && !isSearchPage) {
                iframeRef.current.src = site.url;
              }
            }}
            style={{
              padding: '5px 10px',
              backgroundColor: url === site.url 
                ? theme === 'dark' ? 'rgba(70, 130, 220, 0.5)' : 'rgba(70, 130, 220, 0.2)' 
                : theme === 'dark' ? 'rgba(40, 40, 70, 0.6)' : 'rgba(235, 235, 240, 0.6)',
              color: theme === 'dark' ? 'white' : 'black',
              border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              borderRadius: '15px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              cursor: 'pointer'
            }}
          >
            {site.name}
          </button>
        ))}
      </div>
      
      {/* Content Area */}
      <div style={{
        flex: 1,
        position: 'relative',
        backgroundColor: 'white'
      }}>
        {loading && !error && !isSearchPage && (
          <div style={{ 
            position: 'absolute', 
            top: '0', 
            left: '0', 
            right: '0',
            height: '3px',
            zIndex: 10
          }}>
            <div 
              style={{
                height: '100%',
                width: '20%',
                backgroundColor: '#4a90e2',
                borderRadius: '2px',
                animation: 'browser-loading 1.5s infinite ease-in-out',
                boxShadow: '0 0 10px rgba(74, 144, 226, 0.5)'
              }}
            ></div>
          </div>
        )}
        
        {error ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: '#ff6b6b',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: theme === 'dark' ? 'rgba(20, 20, 35, 0.9)' : 'rgba(240, 240, 245, 0.9)'
          }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', marginBottom: '20px' }}></i>
            <h3>Connection Error</h3>
            <p>{error}</p>
            <p style={{ color: theme === 'dark' ? '#aaa' : '#777', marginTop: '10px' }}>URL: {url}</p>
            
            <div style={{ marginTop: '30px', maxWidth: '500px' }}>
              <p style={{ color: theme === 'dark' ? '#aaa' : '#777', fontSize: '14px', marginBottom: '15px', textAlign: 'left' }}>
                <b>Why this happens:</b> For security reasons, many websites prevent being loaded inside frames in other websites. 
                This is called X-Frame-Options or Content Security Policy.
              </p>
              
              <div style={{ marginTop: '20px' }}>
                <p style={{ fontSize: '14px', marginBottom: '10px', color: theme === 'dark' ? '#ddd' : '#555' }}>Try one of these sites that work in iframes:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center' }}>
                  {safeWebsites.slice(0, 5).map((site, index) => (
                    <button 
                      key={index}
                      onClick={() => {
                        setUrl(site.url);
                        setLoading(true);
                        setError(null);
                        setIsSearchPage(site.url.includes('duckduckgo.com'));
                      }}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: theme === 'dark' ? 'rgba(70, 130, 220, 0.5)' : 'rgba(70, 130, 220, 0.2)',
                        color: theme === 'dark' ? 'white' : '#333',
                        border: 'none',
                        borderRadius: '15px',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      {site.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : isSearchPage ? (
          renderSearchPage()
        ) : (
          <iframe
            ref={iframeRef}
            src={url}
            style={{ 
              width: '100%',
              height: '100%',
              border: 'none' 
            }}
            onLoad={handleLoad}
            onError={handleError}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          />
        )}
      </div>
      
      <style jsx global>{`
        @keyframes browser-loading {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(500%);
          }
        }
      `}</style>
    </div>
  );
};

export default Browser; 